import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, RotateCcw, Check, SwitchCamera, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * CameraCaptureModal — full-screen in-browser camera using getUserMedia.
 * Drivers can take a proof-of-delivery photo without leaving the app.
 *
 * Props:
 *   open (bool), onClose(), onCapture(file: File) — called with a JPEG File
 *   title (str)
 */
const CameraCaptureModal = ({ open, onClose, onCapture, title = 'Take photo' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState('environment'); // rear camera by default
  const [snapshot, setSnapshot] = useState(null); // dataURL preview

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setReady(false);
    setSnapshot(null);
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera API not supported on this browser.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setError(e?.message || 'Could not access camera. Please allow camera permission.');
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facing]);

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL('image/jpeg', 0.85);
    setSnapshot(dataUrl);
  };

  const retake = () => setSnapshot(null);

  const confirm = async () => {
    if (!snapshot) return;
    // Convert dataURL → Blob → File
    const res = await fetch(snapshot);
    const blob = await res.blob();
    const file = new File([blob], `pod-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture?.(file);
    onClose?.();
  };

  const flip = () => setFacing((f) => (f === 'environment' ? 'user' : 'environment'));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="camera-capture-modal">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="font-semibold flex items-center gap-2"><Camera className="h-4 w-4" /> {title}</div>
        <button onClick={onClose} className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close" data-testid="camera-close-btn">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="text-center px-6">
            <p className="text-rose-300 text-sm">{error}</p>
            <p className="text-slate-400 text-xs mt-2">You can still tap &ldquo;Choose from files&rdquo; in the previous screen to upload a photo.</p>
          </div>
        ) : snapshot ? (
          <img src={snapshot} alt="Captured preview" className="max-h-full max-w-full object-contain" data-testid="camera-preview-image" />
        ) : (
          <>
            <video ref={videoRef} playsInline muted className="max-h-full max-w-full object-contain" data-testid="camera-video-feed" />
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="px-4 pb-6 pt-3 flex items-center justify-between gap-3 bg-black">
        {snapshot ? (
          <>
            <Button onClick={retake} variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="camera-retake-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Retake
            </Button>
            <div className="flex-1" />
            <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="camera-confirm-btn">
              <Check className="h-4 w-4 mr-1.5" /> Use photo
            </Button>
          </>
        ) : (
          <>
            <Button onClick={flip} variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="camera-flip-btn">
              <SwitchCamera className="h-4 w-4 mr-1.5" /> Flip
            </Button>
            <button
              onClick={capture}
              disabled={!ready || !!error}
              aria-label="Capture photo"
              className="h-16 w-16 rounded-full bg-white ring-4 ring-white/30 active:scale-95 transition disabled:opacity-40 mx-auto"
              data-testid="camera-shutter-btn"
            />
            <div className="w-[100px]" /> {/* spacer to balance flip button */}
          </>
        )}
      </div>
    </div>
  );
};

export default CameraCaptureModal;
